import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetActiveWebMeetingQuery } from '@store/api/meeting';
import {  showSuccessToast } from '@store/customActions';
import { $Routes } from '@routes';
import { isZoomApp, isWebUser } from '@utils/helpers';
import useProfile from './useProfile';
import { updateParticipantJoinedMeeting } from '@store/meetingSlice';

/**
 * Hook to check for active web meetings and rejoin user automatically
 */
const useActiveWebMeetingCheck = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  const { hasToken, profile } = useProfile();
  
  // Reset hasChecked when profile changes (e.g., after login)
  useEffect(() => {
    if (profile?._id) {
      setHasChecked(false);
    }
  }, [profile?._id]);
  
  // Skip check for Zoom app users
  const shouldCheck = !isZoomApp && isWebUser(profile) && profile?._id && !hasChecked && hasToken;
  
  // Check if user has an active web meeting
  const { 
    data: activeWebMeetingData, 
    isLoading: isCheckingActive,
    isError: hasActiveCheckError 
  } = useGetActiveWebMeetingQuery(undefined, {
    skip: !shouldCheck
  });
  
  useEffect(() => {
    if (!shouldCheck) return;
    
    // Handle active meeting found
    if (activeWebMeetingData?.hasActiveMeeting && activeWebMeetingData.meeting) {
      setIsChecking(true);
      
      console.info('[ActiveWebMeetingCheck]: Found active meeting', {
        meetingId: activeWebMeetingData.meeting._id
      });
      
      // Update meeting state with active meeting
      dispatch(updateParticipantJoinedMeeting(activeWebMeetingData.meeting));
      dispatch(showSuccessToast('Rejoined your active meeting'));
      
      // Only navigate hosts to the agenda page, participants stay on their current page
      const isHost = activeWebMeetingData.meeting.hostId === profile._id;
      if (isHost) {
        const agendaId = activeWebMeetingData.meeting.meetingInstances[0]?.agenda?._id;
        if (agendaId) {
          navigate(`${$Routes.agendaDetailPage}/${agendaId}`);
        }
      } else {
        console.info('[ActiveWebMeetingCheck]: Participant rejoined active meeting - staying on current page');
      }
      
      setIsChecking(false);
      setHasChecked(true);
    }
    // No active meeting found
    else if (!isCheckingActive && !hasActiveCheckError) {
      setHasChecked(true);
    }
  }, [
    shouldCheck,
    activeWebMeetingData,
    isCheckingActive,
    hasActiveCheckError,
    dispatch,
    navigate,
    profile
  ]);
  
  // Handle errors
  useEffect(() => {
    if (hasActiveCheckError) {
      console.error('[ActiveWebMeetingCheck]: Active meeting check failed', {
        hasActiveCheckError
      });
      setHasChecked(true);
    }
  }, [hasActiveCheckError]);
  
  return {
    isChecking: isChecking || isCheckingActive,
    hasChecked
  };
};

export default useActiveWebMeetingCheck;